import React, { useMemo, useState } from "react";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { Button } from "./button";
import { Checkbox } from "./checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "./command";
import { cn } from "../../lib/utils";

/**
 * Phase 176 — checkbox multi-select in a searchable popover.
 *
 * props
 *  options      [{ value, label, hint?, group? }]
 *  value        array of selected values
 *  onChange     (values[]) => void
 *  allLabel     text shown when nothing is selected ("All wards")
 *  noun         singular noun for the summary ("ward" → "3 wards")
 *  disabled     locked control (shows `lockedLabel` if given)
 *  className    trigger classes (height / width)
 */
export const MultiSelect = ({
  options = [], value = [], onChange, allLabel = "All", noun = "item", nounPlural, placeholder = "Search…",
  disabled = false, lockedLabel = null, className = "", testId = "multi-select", align = "start", emptyText = "No matches",
}) => {
  const [open, setOpen] = useState(false);
  const selected = useMemo(() => new Set(value || []), [value]);
  const byValue = useMemo(() => new Map(options.map((o) => [o.value, o])), [options]);
  const plural = nounPlural || `${noun}s`;

  const summary = (() => {
    if (lockedLabel) return lockedLabel;
    if (selected.size === 0) return allLabel;
    if (selected.size === 1) return byValue.get([...selected][0])?.label || allLabel;
    if (selected.size === options.length && options.length > 0) return `All ${options.length} ${plural}`;
    return `${selected.size} ${plural}`;
  })();
  const title = selected.size > 1 ? [...selected].map((v) => byValue.get(v)?.label || v).join(", ") : undefined;

  const toggle = (v) => {
    const next = new Set(selected);
    if (next.has(v)) next.delete(v); else next.add(v);
    onChange([...options.filter((o) => next.has(o.value)).map((o) => o.value)]);   // keep option order
  };
  const selectAll = () => onChange(options.map((o) => o.value));
  const clear = () => onChange([]);

  // group rendering (optional `group` on options keeps the parent → child cascade readable)
  const groups = useMemo(() => {
    const m = new Map();
    options.forEach((o) => { const g = o.group || ""; if (!m.has(g)) m.set(g, []); m.get(g).push(o); });
    return [...m.entries()];
  }, [options]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" role="combobox" aria-expanded={open} disabled={disabled}
          className={cn("h-10 w-full justify-between font-normal px-3", selected.size > 0 && !lockedLabel && "border-primary/50 bg-primary/5", className)}
          title={title} data-testid={testId} data-count={selected.size} data-locked={disabled && lockedLabel ? "true" : undefined}>
          <span className={cn("truncate", selected.size === 0 && !lockedLabel && "text-muted-foreground")}>{summary}</span>
          <span className="flex items-center gap-1 shrink-0 ml-1">
            {selected.size > 0 && !disabled && (
              <span role="button" tabIndex={-1} aria-label={`Clear ${plural}`} data-testid={`${testId}-clear`}
                className="rounded-sm p-0.5 hover:bg-muted text-muted-foreground"
                onClick={(e) => { e.stopPropagation(); clear(); }}
                onPointerDown={(e) => e.stopPropagation()}>
                <X className="h-3.5 w-3.5" />
              </span>
            )}
            <ChevronsUpDown className="h-4 w-4 opacity-50" />
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align={align} className="w-[var(--radix-popover-trigger-width)] min-w-[240px] p-0 z-[1200]" data-testid={`${testId}-popover`}>
        <Command>
          <CommandInput placeholder={placeholder} className="h-9" data-testid={`${testId}-search`} />
          <div className="flex items-center justify-between px-2 py-1.5 border-b text-[11px]">
            <span className="text-muted-foreground" data-testid={`${testId}-summary`}>
              {selected.size} of {options.length} selected
            </span>
            <span className="flex items-center gap-2">
              <button type="button" className="text-primary hover:underline disabled:opacity-40 disabled:no-underline" onClick={selectAll}
                disabled={options.length === 0 || selected.size === options.length} data-testid={`${testId}-select-all`}>Select all</button>
              <span className="text-muted-foreground">·</span>
              <button type="button" className="text-primary hover:underline disabled:opacity-40 disabled:no-underline" onClick={clear}
                disabled={selected.size === 0} data-testid={`${testId}-clear-all`}>Clear</button>
            </span>
          </div>
          <CommandList className="max-h-64">
            <CommandEmpty>{emptyText}</CommandEmpty>
            {groups.map(([g, opts]) => (
              <CommandGroup key={g || "__"} heading={g || undefined}>
                {opts.map((o) => {
                  const on = selected.has(o.value);
                  return (
                    <CommandItem key={o.value} value={`${o.label} ${o.hint || ""} ${o.group || ""}`} onSelect={() => toggle(o.value)}
                      className="gap-2 cursor-pointer" data-testid={`${testId}-option-${o.value}`} data-selected={on ? "true" : "false"}>
                      <Checkbox checked={on} tabIndex={-1} className="pointer-events-none h-4 w-4" aria-hidden="true" />
                      <span className="flex-1 min-w-0">
                        <span className="block truncate text-sm">{o.label}</span>
                        {o.hint && <span className="block truncate text-[11px] text-muted-foreground">{o.hint}</span>}
                      </span>
                      {on && <Check className="h-3.5 w-3.5 text-primary shrink-0" aria-hidden="true" />}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
